import { IHttpService } from 'angular';
import { QRCodePayload } from './models/QRCodePayload';
import { QRAssessmentMetadata } from './models/QRAssessmentMetadata';
import type { ITelemetryBeaconService, TelemetrySeverity, TelemetryEventData } from '../../services/core/TelemetryBeaconService';

interface ProcessedQRResult {
    downloadUrl: string;
    metadata: QRAssessmentMetadata;
}

interface EnvironmentUrls {
    [key: string]: string;
}

export class QRCodeService {
    private recordQRTelemetry(eventType: string, severity: TelemetrySeverity, data: TelemetryEventData): void {
        try {
            this.telemetryBeaconService.record(eventType, severity, data);
        } catch (e) {
            // telemetry must never break core functionality
        }
    }

    private mapErrorToReason(message: string): string {
        if (message.startsWith('Invalid host')) return 'invalid_host';
        if (message.startsWith('Invalid QR code URL')) return 'invalid_url';
        if (message.startsWith('Invalid QR code format') || message.startsWith('Missing required QR')) return 'invalid_format';
        if (message.startsWith('Invalid environment')) return 'invalid_environment';
        if (message.startsWith('Invalid metadata')) return 'invalid_metadata';
        return 'network_error';
    }

    private readonly ENV_URLS: EnvironmentUrls = {
        production: 'https://eassessment.ibo.org',
        uat: 'https://uat-ibeads.net',
        sit: 'https://sit-ibeads.net'
    };

    static $inject = ['$http', 'TelemetryBeaconService'];

    constructor(
        private $http: IHttpService,
        private telemetryBeaconService: ITelemetryBeaconService
    ) {}

    private getBaseUrlFromHost(host: string): string {
        // Accept any host that matches our patterns
        if (host.endsWith('.ibo.org') ||
            host.includes('ibeads.net') ||
            host === 'ib-dev') {
            return `https://${host}`;
        }

        throw new Error('Invalid host in QR code URL');
    }

    private parseUrlPayload(url: string): { baseUrl: string; downloadToken: string } {
        try {
            const urlObj = new URL(url);
            const match = urlObj.pathname.match(/\/player\/pwa-qr\/([^\/]+)/);
            
            if (!match) {
                throw new Error('Invalid QR code URL format');
            }

            return {
                baseUrl: this.getBaseUrlFromHost(urlObj.host),
                downloadToken: match[1]
            };
        } catch (e) {
            throw e instanceof Error ? e : new Error('Invalid QR code URL');
        }
    }

    private getBaseUrl(environment: string): string {
        if (environment === 'production') {
            return this.ENV_URLS.production;
        }
        if (environment === 'uat') {
            return this.ENV_URLS.uat;
        }
        if (environment === 'sit') {
            return this.ENV_URLS.sit;
        }        
        const devMatch = environment.match(/^dev(\d+)$/);
        if (devMatch) {
            return `https://dev${devMatch[1]}-ibeads.net`;
        }
        throw new Error('Invalid environment specified');
    }

    private validateQRPayload(payload: unknown): { environment: string; downloadToken: string } {
        if (!payload || typeof payload !== 'object') {
            throw new Error('Invalid QR code format');
        }

        const qrPayload = payload as QRCodePayload;
        if (!qrPayload.e || !qrPayload.t) {
            throw new Error('Missing required QR code fields');
        }

        return {
            environment: qrPayload.e,
            downloadToken: qrPayload.t
        };
    }

    async processQRCode(qrData: string): Promise<ProcessedQRResult> {
        try {
            let baseUrl: string;
            let downloadToken: string;

            // Check if the QR data is a URL
            if (qrData.startsWith('https')) {
                const urlPayload = this.parseUrlPayload(qrData);
                baseUrl = urlPayload.baseUrl;
                downloadToken = urlPayload.downloadToken;
            } else {
                // Handle legacy JSON format
                try {
                    const payload = JSON.parse(qrData);
                    const { environment, downloadToken: token } = this.validateQRPayload(payload);
                    baseUrl = this.getBaseUrl(environment);
                    downloadToken = token;
                } catch (e) {
                    if (e instanceof SyntaxError) {
                        throw new Error('Invalid QR code format');
                    }
                    throw e;
                }
            }

            const metadataUrl = `${baseUrl}/pwa-api/self_serve_package/metadata/${downloadToken}/`;
            const response = await this.$http.get<QRAssessmentMetadata>(metadataUrl);
            const metadata = response.data;

            if (!metadata.download_url) {
                console.error('Invalid metadata response', JSON.stringify(metadata));
                throw new Error('Invalid metadata response');
            }

            this.recordQRTelemetry('qr.scan_success', 'info', {
                host: new URL(baseUrl).host,
                // A scan happens before login, so the session context is still empty.
                // These are the only fields that say which package was picked up.
                packageType: metadata.package_type || '',
                assessmentUuid: metadata.assessment_uuid || '',
                env: metadata.env || '',
                hasSchoolCode: metadata.school_code != null,
            });

            return { downloadUrl: metadata.download_url, metadata };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            const reason = this.mapErrorToReason(message);
            this.recordQRTelemetry('qr.scan_error', 'warning', { reason, message: message.substring(0, 200) });
            console.error('Error processing QR code:', error);
            throw error instanceof Error ? error : new Error('Unknown error:' + error);
        }
    }
}

export default function registerQRCodeService(services: angular.IModule): void {
    services.service('QRCodeService', QRCodeService);
}
